import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { WhatsAppSession, QRCodeResponse } from '../../../types/api';
import api from '../../../lib/api';
import timeConditionService from '../../../services/timeCondition.service';
import { normalizeStatus } from '../constants';

export function useSession(userId: number) {
  const [currentQrSession, setCurrentQrSession] = useState<string | null>(null);
  const [qrCodeData, setQrCodeData] = useState<QRCodeResponse | null>(null);
  const [isLoadingQr, setIsLoadingQr] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Queries
  const { data: timeConditions = [] } = useQuery({
    queryKey: ['time-conditions'],
    queryFn: () => timeConditionService.getTimeConditions(),
  });

  const { data: sessions, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const response = await api.get('/whatsapp/sessions');
      return Array.isArray(response.data) ? response.data : response.data?.data || [];
    },
    refetchInterval: 5000,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<WhatsAppSession>) => api.post('/whatsapp/sessions', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: WhatsAppSession) =>
      api.put(`/whatsapp/sessions/${data.session_name}`, {
        assistant_id: data.assistant_id || null,
        isDefault: data.isDefault,
        token: data.token,
        engine: data.engine || 'GOWS',
        channel: data.channel || 'all',
        integration_id: data.integration_id || null,
        messages_per_minute: data.messages_per_minute || 2,
        messages_per_hour: data.messages_per_hour || 20,
        messages_time_condition_id: data.messages_time_condition_id || null,
        send_alert_at: data.send_alert_at || null,
        smtp_host: data.smtp_host || null,
        smtp_port: data.smtp_port || null,
        smtp_username: data.smtp_username || null,
        smtp_password: data.smtp_password || null,
        smtp_secure: data.smtp_secure !== false,
        voice_number: data.voice_number || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (session_name: string) => api.delete(`/whatsapp/sessions/${session_name}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const startMutation = useMutation({
    mutationFn: (session_name: string) => api.post(`/whatsapp/sessions/${session_name}/start`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const stopMutation = useMutation({
    mutationFn: (session_name: string) => api.post(`/whatsapp/sessions/${session_name}/stop`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const restartMutation = useMutation({
    mutationFn: (session_name: string) => api.post(`/whatsapp/sessions/${session_name}/restart`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (session_name: string) =>
      api.put(`/whatsapp/sessions/${session_name}`, { isDefault: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });

  // QR Code polling
  const startQrCodePolling = async (sessionName: string) => {
    setCurrentQrSession(sessionName);
    setIsLoadingQr(true);
    setQrError(null);

    try {
      const sessionResponse = await api.get(`/whatsapp/sessions/${sessionName}`);

      // Check if session is using TICKETZ engine
      if (sessionResponse.data.engine === 'TICKETZ') {
        setQrError('QR Code não disponível para sessões com engine TICKETZ');
        return false;
      }

      if (sessionResponse.data.engine === 'DIGISAC') {
        setQrError('QR Code não disponível para sessões com engine DIGISAC');
        return false;
      }

      if (sessionResponse.data.engine === 'GMAIL') {
        setQrError('QR Code não disponível para sessões com engine GMAIL');
        return false;
      }

      const currentStatus = normalizeStatus(sessionResponse.data.status);

      if (currentStatus === 'working') {
        setQrError('Sessão já está conectada');
        return false;
      }

      if (currentStatus !== 'scan_qr_code') {
        setQrError(`Status atual: ${sessionResponse.data.status}`);
        return false;
      }

      const response = await api.get<QRCodeResponse>(`/whatsapp/sessions/${sessionName}/qr`);
      setQrCodeData(response.data);

      if (!response.data.url && normalizeStatus(response.data.status) !== 'working') {
        setQrError('QR Code não disponível. Aguarde alguns segundos.');
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erro ao obter QR code:', error);
      setQrError('Erro ao obter o QR Code. Tente novamente.');
      return false;
    } finally {
      setIsLoadingQr(false);
    }
  };

  const stopQrCodePolling = () => {
    setCurrentQrSession(null);
    setQrCodeData(null);
    setQrError(null);
  };

  return {
    sessions,
    isLoading,
    timeConditions,
    currentQrSession,
    qrCodeData,
    isLoadingQr,
    qrError,
    mutations: {
      create: createMutation,
      update: updateMutation,
      delete: deleteMutation,
      start: startMutation,
      stop: stopMutation,
      restart: restartMutation,
      setDefault: setDefaultMutation,
    },
    qrCode: {
      startPolling: startQrCodePolling,
      stopPolling: stopQrCodePolling,
    },
  };
}
